import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';

// Hierarchy/sizes/pricing config are admin-edited reference data that rarely changes within a
// session - cache them here (chatbot-only, not the shared MasterDataSeriveService, since that's
// also used by admin CRUD screens that need to see their own edits immediately).
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProductionChatbotApiService {
  tpmEndpointUrl = environment.chatbotEndpointIntegration
  constructor(private http: HttpClient, private masterService: MasterDataSeriveService) { }

  private hierarchicalDataCache = new Map<string, CacheEntry<any>>();
  private sizesCache = new Map<string, CacheEntry<any>>();
  private pricingConfigCache = new Map<string, CacheEntry<any>>();

  private isFresh(entry: CacheEntry<any> | undefined): boolean {
    return !!entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS;
  }

  async getHierarchicalData(companyId: number, storeId: number): Promise<any> {
    const key = `${companyId}_${storeId}`;
    const cached = this.hierarchicalDataCache.get(key);
    if (this.isFresh(cached)) return cached!.data;

    const data = await this.masterService.getHierarchicalData(companyId, storeId).toPromise();
    this.hierarchicalDataCache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  async getSizes(companyId: number): Promise<any> {
    const key = `${companyId}`;
    const cached = this.sizesCache.get(key);
    if (this.isFresh(cached)) return cached!.data;

    const data = await this.masterService.getAllSizes(companyId).toPromise();
    this.sizesCache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  async getPricingConfig(companyId: number): Promise<any> {
    const key = `${companyId}`;
    const cached = this.pricingConfigCache.get(key);
    if (this.isFresh(cached)) return cached!.data;

    const data = await this.masterService.getPricingTypeConfig(companyId).toPromise();
    this.pricingConfigCache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  parseCommand(command: string, hierarchicalData: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.tpmEndpointUrl}parse-command`, {
      command,
      hierarchical_data: hierarchicalData
    }, {
      headers: new HttpHeaders({ 'accept': 'application/json', 'Content-Type': 'application/json' })
    }));
  }

  uploadVideo(formData: FormData, companyId:number): Promise<any> {
    // Note: do not set Content-Type header; let browser set multipart boundary
    return firstValueFrom(this.http.post<any>(`${this.tpmEndpointUrl}upload-video?company_id=${companyId}`, formData));
  }
}
